import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from 'i18next';
import {
  Users,
  FileText,
  Calendar,
  Sparkles,
  Layers,
  CheckCircle2,
  Circle,
  ChevronRight,
  X,
  Trophy,
} from "lucide-react";
import { useThemeStore } from "../../store/themeStore";

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  link: string;
  linkText: string;
  completed: boolean;
}

interface OnboardingChecklistProps {
  steps: OnboardingStep[];
  onDismiss: () => void;
  onStepAction?: (stepId: string) => void;
}

export function getDefaultOnboardingSteps(
  t: TFunction,
  hasAccounts: boolean,
  hasPosts: boolean,
  hasScheduled: boolean,
  hasPage: boolean,
): OnboardingStep[] {
  return [
    {
      id: "connect",
      title: t("onboard_step_connect_title", "Connect your first social account"),
      description: t("onboard_step_connect_desc", "Link your YouTube, Instagram, TikTok, or any other platform to start publishing."),
      icon: Users,
      link: "/accounts",
      linkText: t("onboard_step_connect_link", "Connect Account"),
      completed: hasAccounts,
    },
    {
      id: "post",
      title: t("onboard_step_post_title", "Create your first post"),
      description: t("onboard_step_post_desc", "Write and publish your first piece of content across multiple platforms."),
      icon: FileText,
      link: "/compose",
      linkText: t("onboard_step_post_link", "Create Post"),
      completed: hasPosts,
    },
    {
      id: "schedule",
      title: t("onboard_step_schedule_title", "Schedule a post for later"),
      description: t("onboard_step_schedule_desc", "Plan ahead by scheduling content for the best times to post."),
      icon: Calendar,
      link: "/compose",
      linkText: t("onboard_step_schedule_link", "Schedule Post"),
      completed: hasScheduled,
    },
    {
      id: "ai",
      title: t("onboard_step_ai_title", "Try the AI content assistant"),
      description: t("onboard_step_ai_desc", "Let AI help you write captions, generate hashtags, and suggest content ideas."),
      icon: Sparkles,
      link: "/ai",
      linkText: t("onboard_step_ai_link", "Try AI Assistant"),
      completed: false,
    },
    {
      id: "page",
      title: t("onboard_step_page_title", "Build a link-in-bio page"),
      description: t("onboard_step_page_desc", "Create a beautiful landing page for your social profiles in minutes."),
      icon: Layers,
      link: "/builder",
      linkText: t("onboard_step_page_link", "Build Page"),
      completed: hasPage,
    },
  ];
}

export default function OnboardingChecklist({
  steps,
  onDismiss,
  onStepAction,
}: OnboardingChecklistProps) {
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const [expandedStep, setExpandedStep] = useState<string | null>(
    steps.find((s) => !s.completed)?.id || null
  );

  const completedCount = steps.filter((s) => s.completed).length;
  const totalSteps = steps.length;
  const progress = Math.round((completedCount / totalSteps) * 100);
  const allComplete = completedCount === totalSteps;

  if (allComplete) {
    return (
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-6 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-8 translate-x-8" />
        <div className="relative flex items-center gap-4">
          <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
            <Trophy size={28} className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold">{t('onboard_all_set', "You're all set!")}</h3>
            <p className="text-sm text-emerald-100 mt-0.5">
              {t('onboard_complete_message', "You've completed all onboarding steps. You're ready to grow your audience!")}
            </p>
          </div>
          <button
            onClick={onDismiss}
            className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-bold text-gray-900">{t('onboard_getting_started', 'Getting Started')}</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {t('onboard_steps_completed', '{{completed}} of {{total}} steps completed', { completed: completedCount, total: totalSteps })}
            </p>
          </div>
          <button
            onClick={onDismiss}
            className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X size={16} />
          </button>
        </div>
        {/* Progress bar */}
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div
            className="h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%`, backgroundColor: theme.hex.primary }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="divide-y divide-gray-50">
        {steps.map((step) => {
          const isExpanded = expandedStep === step.id;

          return (
            <div key={step.id}>
              <button
                onClick={() => setExpandedStep(isExpanded ? null : step.id)}
                className={`w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-gray-50 transition-colors ${
                  step.completed ? "opacity-60" : ""
                }`}
              >
                {step.completed ? (
                  <CheckCircle2
                    size={22}
                    className="flex-shrink-0"
                    style={{ color: theme.hex.primary }}
                  />
                ) : (
                  <Circle size={22} className="text-gray-300 flex-shrink-0" />
                )}
                <span
                  className={`flex-1 text-sm font-medium ${
                    step.completed ? "text-gray-400 line-through" : "text-gray-800"
                  }`}
                >
                  {step.title}
                </span>
                <ChevronRight
                  size={16}
                  className={`text-gray-400 transition-transform ${
                    isExpanded ? "rotate-90" : ""
                  }`}
                />
              </button>

              {isExpanded && !step.completed && (
                <div className="px-5 pb-4 pl-12">
                  <p className="text-sm text-gray-500 mb-3">{step.description}</p>
                  <Link
                    to={step.link}
                    onClick={() => onStepAction?.(step.id)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg transition-all hover:opacity-90"
                    style={{ backgroundColor: theme.hex.primary }}
                  >
                    {step.linkText}
                    <ChevronRight size={14} />
                  </Link>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
